import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Wrench, Sparkles, ShieldCheck, MapPin, ArrowRight, BadgeCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function AftersalesCustomization() {
  const { t } = useI18n();

  const sections = [
    {
      icon: Wrench,
      title: t("aftersales.afterSalesTitle"),
      lead: t("aftersales.afterSalesLead"),
      bullets: [
        t("aftersales.afterSales1"),
        t("aftersales.afterSales2"),
        t("aftersales.afterSales3"),
      ],
      partner: {
        name: "Garage VW Lede — Van Waeyenbergh",
        address: "Kleiweg 11, 9340 Lede",
        note: t("aftersales.partnerNote"),
      },
    },
    {
      icon: Sparkles,
      title: t("aftersales.customizationTitle"),
      lead: t("aftersales.customizationLead"),
      bullets: [
        t("aftersales.custom1"),
        t("aftersales.custom2"),
        t("aftersales.custom3"),
        t("aftersales.custom4"),
        t("aftersales.custom5"),
        t("aftersales.custom6"),
      ],
    },
    {
      icon: ShieldCheck,
      title: t("aftersales.warrantyTitle"),
      lead: t("aftersales.warrantyLead"),
      bullets: [
        t("aftersales.warranty1"),
        t("aftersales.warranty2"),
        t("aftersales.warranty3"),
      ],
    },
  ];

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-[140px] pb-12 px-5 lg:px-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-sm text-white/50 mb-3">
            <Link to="/" className="hover:text-[#c5a76f]">{t("common.home")}</Link>
            <span className="mx-2">—</span>
            <span className="text-[#c5a76f]">{t("nav.aftersales")}</span>
          </div>
          <div className="text-[#c5a76f] text-xs tracking-[0.3em] uppercase mb-3">
            {t("aftersales.overline")}
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            style={{ fontFamily: "'Familjen Grotesk', serif" }}
          >
            {t("aftersales.heroTitle1")} <span className="text-[#c5a76f]">{t("aftersales.heroTitleAccent")}</span>{" "}
            {t("aftersales.heroTitle2")}
          </h1>
          <p className="mt-5 text-white/65 max-w-2xl text-base leading-relaxed">
            {t("aftersales.heroLead")}
          </p>
        </div>
      </section>

      {/* Content sections */}
      <section className="py-20 px-5 lg:px-8">
        <div className="max-w-[1200px] mx-auto space-y-10">
          {sections.map((s, idx) => (
            <article
              key={idx}
              data-testid={`aftersales-card-${idx}`}
              className="group bg-[#242424] border border-white/5 rounded-lg p-7 md:p-10 hover:border-[#c5a76f]/30 transition-colors"
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-14 h-14 rounded-full bg-[#c5a76f]/10 border border-[#c5a76f]/30 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-[#c5a76f]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className="text-2xl md:text-3xl font-bold text-white mb-3"
                    style={{ fontFamily: "'Familjen Grotesk', serif" }}
                  >
                    {s.title}
                  </h2>
                  <p className="text-white/70 leading-relaxed mb-6">{s.lead}</p>

                  <ul className="space-y-2.5">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                        <BadgeCheck className="w-4 h-4 text-[#c5a76f] mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {s.partner && (
                    <div className="mt-7 p-5 rounded-md border border-[#c5a76f]/25 bg-[#1a1a1a]">
                      <div className="text-[#c5a76f] text-[10px] tracking-[0.25em] uppercase mb-2">
                        {t("aftersales.partnerLabel")}
                      </div>
                      <div className="text-white font-semibold text-lg mb-1">
                        {s.partner.name}
                      </div>
                      <div className="flex items-center gap-2 text-white/65 text-sm mb-3">
                        <MapPin className="w-4 h-4 text-[#c5a76f]" /> {s.partner.address}
                      </div>
                      <p className="text-white/55 text-sm leading-relaxed">{s.partner.note}</p>
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[1200px] mx-auto bg-[#242424] border border-[#c5a76f]/30 rounded-lg p-10 md:p-14 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: "'Familjen Grotesk', serif" }}
          >
            {t("aftersales.ctaTitle1")} <span className="text-[#c5a76f]">{t("aftersales.ctaTitleAccent")}</span>?
          </h2>
          <p className="text-white/65 max-w-xl mx-auto mb-8">{t("aftersales.ctaText")}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              data-testid="aftersales-contact-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded"
            >
              {t("common.contactUs")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/stock"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#c5a76f] text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors text-sm font-semibold tracking-widest uppercase rounded"
            >
              {t("nav.offer")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
